import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  Inbox, 
  Trash2, 
  FileText,
  Search,
  Phone,
  Eye,
  Edit3,
  TrendingUp,
  TrendingDown,
  DownloadCloud,
  Filter as FilterIcon,
  X,
  ChevronDown
} from 'lucide-react';

const statusOptions = [
  { value: '', label: 'All Status', icon: Inbox },
  { value: 'pending', label: 'Pending', icon: AlertTriangle },
  { value: 'contacted', label: 'Contacted', icon: Phone },
  { value: 'enrolled', label: 'Enrolled', icon: CheckCircle },
  { value: 'not_interested', label: 'Not Interested', icon: X },
];

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Check admin session
  useEffect(() => {
    api.get('/enquiries', { params: { limit: 1 } })
      .then(() => {})
      .catch(() => window.location.href = '/admin/login');
  }, []);

  // Fetch stats and enquiries
  useEffect(() => {
    setLoading(true);
    setError('');
    const fetchStats = api.get('/enquiries/statistics');
const fetchEnquiries = statusFilter
? api.get(`/enquiries/status/${statusFilter}`, { params: { page } })
: api.get('/enquiries', { params: { page } });
    Promise.all([fetchStats, fetchEnquiries])
      .then(([statsRes, enquiriesRes]) => {
        setStats(statsRes.data.data);
        setEnquiries(enquiriesRes.data.data.enquiries);
        setTotalPages(enquiriesRes.data.data.totalPages);
      })
      .catch(() => setError('Failed to load data. Please try again.'))
      .finally(() => setLoading(false));
  }, [statusFilter, page]);

  const handleStatusChange = (id, newStatus) => {
    api.patch(`/enquiries/${id}/status`, { status: newStatus })
      .then(() => {
        setEnquiries(enquiries => enquiries.map(e => e.id === id ? { ...e, status: newStatus } : e));
      })
      .catch(() => setError('Failed to update status.'));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    setDeleting(true);
    api.delete(`/enquiries/${id}`)
      .then(() => {
        setEnquiries(enquiries => enquiries.filter(e => e.id !== id));
      })
      .catch(() => setError('Failed to delete enquiry.'))
      .finally(() => setDeleting(false));
  };

  const handleView = async (id) => {
    try {
      const response = await api.get(`/enquiries/${id}`);
      setSelectedEnquiry(response.data.data);
      setShowViewModal(true);
    } catch (err) {
      setError('Failed to load enquiry details.');
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await api.get(`/enquiries/${id}`);
      setSelectedEnquiry(response.data.data);
      setShowEditModal(true);
    } catch (err) {
      setError('Failed to load enquiry details.');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedEnquiry) return;
    
    try {
      await api.put(`/enquiries/${selectedEnquiry.id}`, {
        name: selectedEnquiry.name,
        email: selectedEnquiry.email,
        phone: selectedEnquiry.phone,
        message: selectedEnquiry.message,
        course: selectedEnquiry.course,
        status: selectedEnquiry.status
      });

      // Update the local state
      setEnquiries(enquiries => enquiries.map(e => 
        e.id === selectedEnquiry.id ? selectedEnquiry : e
      ));
      
      setShowEditModal(false);
      setSelectedEnquiry(null);
    } catch (err) {
      setError('Failed to update enquiry.');
    }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const response = await api.get('/enquiries/export', { 
        params: { format, status: statusFilter },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `enquiries-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to export data.');
    } finally {
      setExporting(false);
    }
  };

  const filteredEnquiries = (enquiries || []).filter(enq => 
    enq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    enq.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    enq.phone.includes(searchTerm) ||
    enq.course.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatCard = ({ title, value, icon: Icon, color, trend, trendValue }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-soft border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{value}</p>
          <div className="flex items-center mt-1">
            {trend === 'up' ? (
              <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="w-3 h-3 text-red-500 mr-1" />
            )}
            <span className={`text-xs font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trendValue}%
            </span>
          </div>
        </div>
        <div className={`p-2 rounded-lg ${color.icon} bg-opacity-10`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-3 mt-1">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-1">Dashboard</h1>
          <p className="text-xs text-gray-600 dark:text-gray-400">Manage enquiries and monitor performance</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="btn btn-primary-gradient btn-sm flex items-center px-3 py-2 text-xs"
          >
            <DownloadCloud className="w-3 h-3 mr-1" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className="btn btn-outline btn-sm flex items-center px-3 py-2 text-xs"
          >
            <FileText className="w-3 h-3 mr-1" />
            Export Excel
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 px-3 py-2 rounded-lg text-center text-xs border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mb-3 text-primary-600" />
          <p className="text-sm text-gray-700 dark:text-gray-300">Loading dashboard...</p>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <StatCard
                title="Total Enquiries"
                value={stats.total}
                icon={Users}
                color={{ icon: 'bg-blue-500' }}
                trend="up"
                trendValue={12}
              />
              <StatCard
                title="Pending"
                value={stats.pending}
                icon={AlertTriangle}
                color={{ icon: 'bg-yellow-500' }}
                trend="down"
                trendValue={5}
              />
              <StatCard
                title="Contacted"
                value={stats.contacted}
                icon={Phone}
                color={{ icon: 'bg-green-500' }}
                trend="up"
                trendValue={8}
              />
              <StatCard
                title="Enrolled"
                value={stats.enrolled}
                icon={CheckCircle}
                color={{ icon: 'bg-purple-500' }}
                trend="up"
                trendValue={15}
              />
            </div>
          )}

          {/* Filters and Search */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-soft border border-gray-100 dark:border-gray-700 mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search enquiries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>

                {/* Status Filter */}
                <div className="relative">
                  <select
                    className="appearance-none pl-3 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white cursor-pointer text-sm"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  >
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="btn btn-outline btn-sm flex items-center text-xs"
                >
                  <FilterIcon className="w-3 h-3 mr-1" />
                  Filters
                </button>
                
                <div className="text-xs text-gray-500">
                  Page {page} of {totalPages}
                </div>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Date Range</label>
                    <input
                      type="date"
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Course</label>
                    <select className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-xs">
                      <option value="">All Courses</option>
                      <option value="web-development">Web Development</option>
                      <option value="data-science">Data Science</option>
                      <option value="mobile-development">Mobile Development</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Sort By</label>
                    <select className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-xs">
                      <option value="date">Date</option>
                      <option value="name">Name</option>
                      <option value="status">Status</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table Section */}
          <div className="overflow-x-auto w-full rounded-lg shadow border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs" style={{ minWidth: '900px' }}>
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[120px]">Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[150px]">Contact</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[140px]">Course</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[100px]">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[80px]">Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[80px]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                {filteredEnquiries.map((enq) => (
                  <tr key={enq.id} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap min-w-[120px]">{enq.name}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[150px]">
                      <div className="truncate text-xs">{enq.email}</div>
                      <div className="truncate text-xs">{enq.phone}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[140px] truncate">{enq.course}</td>
                    <td className="px-3 py-2 whitespace-nowrap min-w-[100px]">
                      <select
                        className="rounded-full px-2 py-1 text-xs font-medium focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200 w-full"
                        value={enq.status}
                        onChange={e => handleStatusChange(enq.id, e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="enrolled">Enrolled</option>
                        <option value="not_interested">Not Interested</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap min-w-[80px] text-xs">{new Date(enq.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap min-w-[80px]">
                      <div className="flex items-center space-x-1">
                        <button
                          className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          title="View Details"
                          onClick={() => handleView(enq.id)}
                        >
                          <Eye className="w-3 h-3" />
                        </button>
                        <button
                          className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                          title="Edit"
                          onClick={() => handleEdit(enq.id)}
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                        <button
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          onClick={() => handleDelete(enq.id)}
                          disabled={deleting}
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  Showing page {page} of {totalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* View Modal */}
      {showViewModal && selectedEnquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Enquiry Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Name</label>
                <p className="text-sm text-gray-900 dark:text-white">{selectedEnquiry.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Email</label>
                <p className="text-sm text-gray-900 dark:text-white">{selectedEnquiry.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Phone</label>
                <p className="text-sm text-gray-900 dark:text-white">{selectedEnquiry.phone}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Course</label>
                <p className="text-sm text-gray-900 dark:text-white">{selectedEnquiry.course}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Message</label>
                <p className="text-sm text-gray-900 dark:text-white">{selectedEnquiry.message}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</label>
                <p className="text-sm text-gray-900 dark:text-white">{selectedEnquiry.status}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Created</label>
                <p className="text-sm text-gray-900 dark:text-white">{new Date(selectedEnquiry.created_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEnquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Enquiry</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Name</label>
                <input
                  type="text"
                  value={selectedEnquiry.name}
                  onChange={(e) => setSelectedEnquiry({...selectedEnquiry, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Email</label>
                <input
                  type="email"
                  value={selectedEnquiry.email}
                  onChange={(e) => setSelectedEnquiry({...selectedEnquiry, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Phone</label>
                <input
                  type="text"
                  value={selectedEnquiry.phone}
                  onChange={(e) => setSelectedEnquiry({...selectedEnquiry, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Course</label>
                <input
                  type="text"
                  value={selectedEnquiry.course}
                  onChange={(e) => setSelectedEnquiry({...selectedEnquiry, course: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Message</label>
                <textarea
                  value={selectedEnquiry.message}
                  onChange={(e) => setSelectedEnquiry({...selectedEnquiry, message: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</label>
                <select
                  value={selectedEnquiry.status}
                  onChange={(e) => setSelectedEnquiry({...selectedEnquiry, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="enrolled">Enrolled</option>
                  <option value="not_interested">Not Interested</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard; 